import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_ALARMS_REPORT_LIST,
	GET_REPORT_PARAM_TYPE_LIST,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_FALLBACK_LIMIT,
	LOOKUP_LIMIT,
	optionalText,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {
	normalizeSearchFields,
	resolveByFields,
} from '../../core/fieldSearch';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

let typeLabelsPromise = null;

/**
 * Load Report Type key-to-label mapping from Canvas.
 * @return {Promise<object>} type labels by key
 */
function loadTypeLabels() {
	if (!typeLabelsPromise) {
		typeLabelsPromise = axios
			.get(`${ENDPOINT_URL}${GET_REPORT_PARAM_TYPE_LIST}?limit=50&offset=0`)
			.then(({data}) => decodeAgentResponse(data)?.data || {})
			.catch(() => ({}));
	}
	return typeLabelsPromise;
}

/**
 * Return a report row identifier.
 * @param {object} item Canvas report row
 * @return {*} identifier
 */
function reportId(item) {
	return item?.report_id ?? item?.id;
}

/**
 * Map API type keys to the Type column labels Canvas already serves.
 * @param {*} value API report_type
 * @param {object} typeLabels key to label map
 * @return {string} display type
 */
function reportTypeLabel(value, typeLabels) {
	if (typeof value === 'string' && value.trim()) return value.trim();
	if (!Array.isArray(value)) return optionalText(value) || '';
	return value
		.map((key) => typeLabels[String(key)] || String(key))
		.filter(Boolean)
		.join(', ');
}

/**
 * Display label for a stored value, read from the catalogue the agent sent.
 * @param {object} field normalized search field
 * @param {*} stored stored attribute
 * @return {string|null} catalogue label
 */
function labelFromChoices(field, stored) {
	if (!field) return stored == null || stored === '' ? null : String(stored);
	if (stored == null || stored === '') return leftoverLabel(field);
	const text = String(stored).trim();
	for (const choice of field.choices) {
		const names = [
			choice.id,
			choice.label,
			...choice.values,
			...choice.aliases,
		];
		if (names.some((name) => String(name) === text)) {
			return choice.label;
		}
	}
	return leftoverLabel(field);
}

/**
 * The choice that is not a stored encoding - One Time, or no interval.
 * @param {object} field normalized search field
 * @return {string|null} leftover label
 */
function leftoverLabel(field) {
	const choices = field.choices || [];
	return choices[choices.length - 1]?.label || null;
}

/**
 * Copy a Canvas row with catalogued fields written as the UI names them.
 * @param {object} item Canvas report row
 * @param {object} typeLabels key to label map
 * @param {*} searchFields field catalogue sent with the lookup
 * @return {object} row the generic lookup can read
 */
function rowForSearch(item, typeLabels, searchFields) {
	const fields = normalizeSearchFields(searchFields);
	const next = {
		...item,
		report_type: reportTypeLabel(item.report_type, typeLabels),
		created_at: optionalText(item.created_at),
	};
	fields.forEach((field) => {
		if (field.id === 'report_type' || field.id === 'created_at') return;
		for (const attribute of field.attributes) {
			if (!Object.prototype.hasOwnProperty.call(item, attribute)) continue;
			const labelled = labelFromChoices(field, item[attribute]);
			if (labelled != null) {
				next[attribute] = labelled;
				return;
			}
		}
	});
	return next;
}

/**
 * Normalize a Canvas report row.
 * @param {object} item Canvas report row
 * @return {object|null} agent candidate
 */
function normalizeReport(item) {
	const id = reportId(item);
	const name = item?.report_name;
	if (id == null || !name) return null;
	return {
		entity_type: 'report',
		entity_id: String(id),
		name: String(name),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Load report rows, paging until the lookup cap.
 * @param {string|null} search name search
 * @param {number} limit maximum rows
 * @return {Promise<Array>} rows
 */
async function loadRows(search, limit) {
	const rows = [];
	const pageSize = Math.min(Math.max(limit, 1), LOOKUP_LIMIT);
	let offset = 0;
	while (rows.length < limit) {
		const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
		const url = `${ENDPOINT_URL}${GET_ALARMS_REPORT_LIST}` +
			`?limit=${pageSize}&offset=${offset}${searchParam}`;
		const {data} = await axios.get(url);
		const page = responseRows(decodeAgentResponse(data) || {});
		rows.push(...page);
		if (page.length < pageSize) break;
		offset += pageSize;
	}
	return rows.slice(0, limit);
}

/**
 * Rank loaded rows through the catalogue the agent sent.
 * @param {Array} rows Canvas rows
 * @param {object} typeLabels key to label map
 * @param {object} params resolver parameters
 * @param {string} query lookup query
 * @param {number} limit maximum candidates
 * @param {boolean} [fuzzy] allow near-miss matches
 * @return {Array} ranked candidates
 */
function candidatesFromRows(rows, typeLabels, params, query, limit, fuzzy = false) {
	return resolveByFields({
		rows: rows.map((row) => rowForSearch(row, typeLabels, params.search_fields)),
		normalizeRow: normalizeReport,
		query,
		limit,
		searchFields: params.search_fields,
		rank: rankLookupCandidates,
		fallbackValues: (candidate) => [candidate?.name],
		fuzzy,
	});
}

/**
 * Resolve report candidates, paging past the first table page.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveReports(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const typeLabels = await loadTypeLabels();
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('report', rows, reportId);
		return {
			candidates: candidatesFromRows(
				rows, typeLabels, params, '', limit,
			).slice(0, limit),
		};
	}
	let rows = await loadRows(query, LOOKUP_LIMIT);
	let candidates = candidatesFromRows(
		rows, typeLabels, params, query, LOOKUP_LIMIT,
	);
	if (!candidates.length) {
		rows = await loadRows(null, LOOKUP_FALLBACK_LIMIT);
		candidates = candidatesFromRows(
			rows, typeLabels, params, query, LOOKUP_LIMIT,
		);
		if (!candidates.length && params.fuzzy === true) {
			candidates = candidatesFromRows(
				rows, typeLabels, params, query, LOOKUP_LIMIT, true,
			);
		}
	}
	replaceResolvedEntities('report', rows, reportId);
	return {candidates};
}

/**
 * Return a retained report row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedReport(entityId) {
	return getResolvedEntity('report', entityId);
}
